/**
 * Drives the @sss/sync Outbox on the device (ADR-0006): flushes after every
 * enqueue, when the browser comes online, when the app returns to the
 * foreground, and every minute. Exposes a reactive status for the sync
 * indicator ("3 items waiting to sync") and the 24-hour warning banner.
 */
import { Outbox, UNSYNCED_WARNING_MS, type DrainReport, type FlushReport, type OutboxItem, type OutboxOp, type Transport } from '@sss/sync';
import type { OfflineStorage } from '../offline/types';

export interface RejectedItem {
  id: string;
  entityId: string;
  op: OutboxOp;
  error: string;
}

export interface SyncStatus {
  pending: number;
  rejected: number;
  oldestPendingAgeMs: number | null;
  /** Unsynced work older than 24 h — show the red banner. */
  stale: boolean;
  online: boolean;
  flushing: boolean;
  lastFlushAt: number | null;
  rejectedItems: RejectedItem[];
  /** Pending item count per survey id. */
  byEntity: Record<string, number>;
}

export interface SyncServiceOptions {
  now?: () => number;
  intervalMs?: number;
  isOnline?: () => boolean;
  newId?: () => string;
}

const INITIAL: SyncStatus = {
  pending: 0,
  rejected: 0,
  oldestPendingAgeMs: null,
  stale: false,
  online: true,
  flushing: false,
  lastFlushAt: null,
  rejectedItems: [],
  byEntity: {},
};

export class SyncService {
  readonly outbox: Outbox;
  private status: SyncStatus = INITIAL;
  private readonly listeners = new Set<(s: SyncStatus) => void>();
  private readonly now: () => number;
  private readonly isOnline: () => boolean;
  private timer: ReturnType<typeof setInterval> | null = null;
  private cleanup: (() => void)[] = [];

  constructor(
    private readonly storage: OfflineStorage,
    transport: Transport,
    private readonly opts: SyncServiceOptions = {},
  ) {
    this.now = opts.now ?? (() => Date.now());
    this.isOnline = opts.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    this.outbox = new Outbox(storage, transport, { now: this.now, newId: opts.newId });
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribe(listener: (s: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((l) => l(this.status));
  }

  /** Recompute counts from the persisted outbox. */
  async refresh(): Promise<SyncStatus> {
    const items = await this.storage.all();
    const stats = await this.outbox.stats();
    const rejectedItems = items
      .filter((i) => i.state === 'REJECTED')
      .map((i) => ({ id: i.id, entityId: i.entityId, op: i.op, error: i.lastError ?? 'REJECTED' }));
    this.emit({
      pending: stats.pending,
      rejected: stats.rejected,
      oldestPendingAgeMs: stats.oldestPendingAgeMs,
      stale: (stats.oldestPendingAgeMs ?? 0) > UNSYNCED_WARNING_MS,
      online: this.isOnline(),
      rejectedItems,
      byEntity: stats.byEntity,
    });
    return this.status;
  }

  async enqueue<P>(op: OutboxOp, entityId: string, payload: P, opts: { flush?: boolean } = {}): Promise<OutboxItem<P>> {
    const item = await this.outbox.enqueue(op, entityId, payload);
    await this.refresh();
    if (opts.flush !== false) void this.flush();
    return item;
  }

  private chain: Promise<unknown> = Promise.resolve();

  /**
   * Send what is due. Calls run one after another, so an item enqueued while
   * a flush is in progress is picked up by the next pass rather than missed.
   */
  flush(): Promise<FlushReport | null> {
    const run = this.chain.then(
      () => this.flushOnce(),
      () => this.flushOnce(),
    );
    this.chain = run.catch(() => {});
    return run;
  }

  private async flushOnce(): Promise<FlushReport | null> {
    if (!this.isOnline()) {
      await this.refresh();
      return null;
    }
    this.emit({ flushing: true });
    try {
      const report = await this.outbox.flush();
      this.emit({ lastFlushAt: this.now() });
      return report;
    } finally {
      this.emit({ flushing: false });
      await this.refresh();
    }
  }

  /**
   * Send everything still queued, ignoring backoff, until the outbox is empty,
   * stuck, or `timeoutMs` has passed (used before wiping the device).
   */
  drain(timeoutMs: number, opts: { pauseMs?: number; sleep?: (ms: number) => Promise<void> } = {}): Promise<DrainReport> {
    const run = this.chain.then(async () => {
      if (!this.isOnline()) return { remaining: (await this.storage.all()).length, passes: 0, timedOut: false };
      this.emit({ flushing: true });
      try {
        return await this.outbox.drain({ timeoutMs, ...opts });
      } finally {
        this.emit({ flushing: false, lastFlushAt: this.now() });
        await this.refresh();
      }
    });
    this.chain = run.catch(() => {});
    return run;
  }

  async retry(entityId: string): Promise<void> {
    await this.outbox.retryEntity(entityId);
    await this.flush();
  }

  async discard(entityId: string): Promise<number> {
    const n = await this.outbox.discardEntity(entityId);
    await this.refresh();
    return n;
  }

  /** Start background triggers. Returns a stop function. */
  start(): () => void {
    if (this.timer) return () => this.stop();
    const flush = () => void this.flush().catch(() => {});
    const onOnline = () => {
      this.emit({ online: true });
      flush();
    };
    const onOffline = () => this.emit({ online: false });
    const onVisible = () => {
      if (document.visibilityState === 'visible') flush();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    // Capacitor fires 'resume' on the document when a native app returns to the foreground.
    document.addEventListener('resume', flush);
    this.cleanup = [
      () => window.removeEventListener('online', onOnline),
      () => window.removeEventListener('offline', onOffline),
      () => document.removeEventListener('visibilitychange', onVisible),
      () => document.removeEventListener('resume', flush),
    ];
    this.timer = setInterval(flush, this.opts.intervalMs ?? 60_000);
    void this.refresh().then(flush);
    return () => this.stop();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.cleanup.forEach((c) => c());
    this.cleanup = [];
  }
}
